import React, { useState } from 'react';
import { ShieldCheck, AlertTriangle } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

interface AdminReopenModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onReopen: (reason: string, newStatus?: string) => Promise<boolean>;
  loading: boolean;
}

const STATUS_OPTIONS = [
  { value: 'in_progress', label: 'In Progress' },
  { value: 'accepted', label: 'Accepted' },
  { value: 'completed_pending_review', label: 'Completed Pending Review' }
];

export const AdminReopenModal: React.FC<AdminReopenModalProps> = ({
  open,
  onOpenChange,
  onReopen,
  loading
}) => {
  const [reopenReason, setReopenReason] = useState('');
  const [newStatus, setNewStatus] = useState('in_progress');

  const handleReopen = async () => {
    if (!reopenReason.trim()) return;
    
    const success = await onReopen(reopenReason.trim(), newStatus);
    if (success) {
      setReopenReason('');
      setNewStatus('in_progress');
      onOpenChange(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-primary" />
            Reopen Archived Job
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="flex items-start gap-2 p-3 rounded-lg bg-warning/10 border border-warning/20">
            <AlertTriangle className="h-4 w-4 text-warning mt-0.5 flex-shrink-0" />
            <div className="text-sm">
              <div className="font-medium text-warning">Warning</div>
              <div className="text-muted-foreground mt-1">
                Reopening this job will restore edit capabilities and remove archived status. 
                This action will be logged for audit purposes.
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="reopen-reason">
              Reason for Reopening *
            </Label>
            <Textarea
              id="reopen-reason"
              placeholder="Explain why this job needs to be reopened..."
              value={reopenReason}
              onChange={(e) => setReopenReason(e.target.value)}
              rows={3}
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="new-status">
              New Job Status
            </Label>
            <Select value={newStatus} onValueChange={setNewStatus}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {STATUS_OPTIONS.map((status) => (
                  <SelectItem key={status.value} value={status.value}>
                    {status.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <DialogFooter>
          <Button 
            variant="outline" 
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleReopen}
            disabled={loading || !reopenReason.trim()}
          >
            {loading ? "Reopening..." : "Reopen Job"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};